
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MdCheckCircle, MdArrowBack, MdArrowForward, MdInfo } from 'react-icons/md';
import axios from 'axios';
import Select from 'react-select';
import { GET_COMPLETED_REQ_DETAILS_API, GET_PENDING_COMPARE_DETAILS_API, CREATE_FINAL_PO_API, GET_ALL_UNITS_API, SEND_WHATSAPP_PDF_API, GET_ALL_PRIORITY_API, BASE_API } from '../config/API';
import Loader from '../components/common/Loader';
import Swal from 'sweetalert2';
import { FaWhatsapp } from 'react-icons/fa';
import './ComparePODetail.css';

const ComparePODetail = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromTab = location.state?.fromTab;

    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading details...');
    const [currentPO, setCurrentPO] = useState(null);
    const [items, setItems] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [quotes, setQuotes] = useState({});
    const [units, setUnits] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [selectedPriority, setSelectedPriority] = useState(null);

    // Review Modal State
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewData, setReviewData] = useState([]);

    const [whatsappLoading, setWhatsappLoading] = useState(false);
    const [whatsappProgress, setWhatsappProgress] = useState({ current: 0, total: 0, vendorName: '' });


    useEffect(() => {
        fetchUnits();
        fetchPriorities();
        if (requestId) {
            fetchCompareDetails();
        }
    }, [requestId]);


    const fetchUnits = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_UNITS_API, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status) {
                setUnits(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    const fetchPriorities = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_PRIORITY_API, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.status) {
                const raw = response.data.data || [];
                console.log('RAW priority[0]:', raw[0]); // <-- check this once
                const priorityOptions = raw.map(p => ({
                    value: p.id ?? p._id ?? p.priorityId ?? p.priority_id ?? p.Id ?? p.PriorityId,
                    label: p.priorityName ?? p.priority_name ?? p.name
                }));
                setPriorities(priorityOptions);
            }
        } catch (error) {
            console.error('Error fetching priorities:', error);
        }
    };

    const fetchCompareDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');

            let response;
            let data = null;

            // Step 1: Try Pending API (Skip if explicitly coming from Completed tab)
            if (fromTab !== 'Completed') {
                try {
                    response = await axios.get(GET_PENDING_COMPARE_DETAILS_API(requestId), {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.data.status) {
                        data = response.data.data || response.data.requestData;
                    }
                } catch (err) {
                    console.warn("Pending API fallback trip:", err.message);
                }
            }

            // Step 2: Fallback to Completed API if first one failed OR if coming from Completed tab
            if (!data && fromTab !== 'Pending') {
                try {
                    response = await axios.get(GET_COMPLETED_REQ_DETAILS_API(requestId), {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.data.status) {
                        data = response.data.data || response.data.requestData;
                    }
                } catch (err) {
                    console.error("Critical: Both APIs failed to fetch details.", err);
                }
            }

            if (data) {
                setCurrentPO({
                    id: data.id,
                    requestNumber: data.reqNumber || `REQ-${data.id}`,
                    project: data.ProjectMaster?.projectName || data.Project?.projectName || null,
                    date: data.date || new Date().toISOString().split('T')[0],
                    tillDate: data.validTill || data.tillDate || null,
                    particular: data.particular || null,
                    projectId: data.projectId,
                    status: data.status,
                    priorityId: data.priorityId
                });

                // Items - use the main RequestItems list
                const itemMapping = (data.RequestItems || []).map(item => ({
                    id: item.id,
                    name: item.Product?.productName || null,
                    productId: item.productId,
                    productDescription: item.productDescription,
                    qty: item.qty || 0,
                    size: item.size || '-',
                    unit: item.Unit?.unitName || '-',
                    unitId: item.unitId
                }));
                setItems(itemMapping);

                // Vendors and Quotes
                const uniqueVendors = [];
                const newQuotes = {};

                itemMapping.forEach(item => {
                    newQuotes[item.id] = {};
                });

                (data.RequestVendors || []).forEach(rv => {
                    const vendor = rv.Vendor;
                    if (vendor) {
                        uniqueVendors.push({
                            id: vendor.id,
                            reqVendorId: rv.id,
                            name: vendor.vendorName,
                            companyName: vendor.companyName,
                            whatsappNumber: vendor.whatsappNumber || vendor.whatsAppNumber || vendor.contactNumber || '',
                            total: 0
                        });

                        // Map quotes for this vendor
                        (rv.VendorsRequests || []).forEach(vr => {
                            if (newQuotes[vr.reqItemId]) {
                                newQuotes[vr.reqItemId][vendor.id] = parseFloat(vr.rate) || 0;
                            }
                        });
                    }
                });

                setVendors(uniqueVendors);
                setQuotes(newQuotes);
            } else {
                setCurrentPO(null);
            }
        } catch (error) {
            console.error('Error fetching compare details:', error);
        } finally {
            setLoading(false);
        }
    };

    const getLowestRate = (itemId) => {
        if (!quotes[itemId]) return 0;
        const rates = Object.values(quotes[itemId]);
        return rates.length ? Math.min(...rates) : 0;
    };

    const [selectedItems, setSelectedItems] = useState({}); // { itemId: [vendorId1, vendorId2] }

    const handleItemSelect = (itemId, vendorId) => {
        setSelectedItems(prev => {
            const currentVendors = prev[itemId] || [];
            if (currentVendors.includes(vendorId)) {
                // Remove vendor
                const newVendors = currentVendors.filter(v => v !== vendorId);
                return { ...prev, [itemId]: newVendors };
            } else {
                // Add vendor
                return { ...prev, [itemId]: [...currentVendors, vendorId] };
            }
        });
    };

    const getSelectedTotal = () => {
        let total = 0;
        Object.entries(selectedItems).forEach(([itemIdStr, vendorIds]) => {
            const itemId = parseInt(itemIdStr);
            const item = items.find(i => i.id === itemId);
            if (item && vendorIds && vendorIds.length > 0) {
                vendorIds.forEach(vendorId => {
                    if (quotes[itemId] && quotes[itemId][vendorId]) {
                        total += quotes[itemId][vendorId] * (item.qty || 0);
                    }
                });
            }
        });
        return total;
    };

    const handleVendorSelectAll = (vendorId) => {
        // Are all items selected for this vendor?
        const allSelected = items.every(item => selectedItems[item.id]?.includes(vendorId));

        setSelectedItems(prev => {
            const newState = { ...prev };
            items.forEach(item => {
                const current = newState[item.id] || [];
                if (allSelected) {
                    // Deselect this vendor for all items
                    newState[item.id] = current.filter(v => v !== vendorId);
                } else {
                    // Select this vendor (if not already)
                    if (!current.includes(vendorId)) {
                        newState[item.id] = [...current, vendorId];
                    }
                }
            });
            return newState;
        });
    };

    // Check if at least one item has a vendor selected
    const hasItemSelected = Object.values(selectedItems).some(vendorsIds => vendorsIds && vendorsIds.length > 0);

    const handleReviewClick = () => {
        if (!hasItemSelected) {
            Swal.fire('No Selection', 'Please select at least one vendor for an item before approving.', 'warning');
            return;
        }

        // Group selected items by vendor for review
        const vendorGroups = {};

        Object.entries(selectedItems).forEach(([itemIdStr, vendorIds]) => {
            const itemId = parseInt(itemIdStr);
            const item = items.find(i => i.id === itemId);
            if (!item || !vendorIds) return;

            vendorIds.forEach(vendorId => {
                const vendor = vendors.find(v => v.id === vendorId);
                const rate = quotes[itemId][vendorId];
                const total = rate * item.qty;

                if (!vendorGroups[vendorId]) {
                    vendorGroups[vendorId] = {
                        reqVendorId: vendor.reqVendorId,
                        vendorId: vendorId,
                        vendorName: vendor.name,
                        whatsappNumber: vendor.whatsappNumber,
                        status: "pending",
                        items: []
                    };
                }

                vendorGroups[vendorId].items.push({
                    reqItemId: itemId,
                    productId: item.productId,
                    productName: item.name,
                    productDescription: item.productDescription,
                    size: item.size === '-' ? '' : item.size,
                    unit: item.unit === '-' ? '' : item.unit,
                    unitId: item.unitId,
                    qty: item.qty,
                    rate: rate,
                    total: total
                });
            });
        });

        const reviewArray = Object.values(vendorGroups);
        console.log("Review Data with WhatsApp Numbers:", reviewArray);
        setReviewData(reviewArray);
        setShowReviewModal(true);
    };

    const handleReviewChange = (vendorIndex, itemIndex, field, value) => {
        const newReviewData = [...reviewData];
        const item = newReviewData[vendorIndex].items[itemIndex];

        item[field] = value;

        // Recalculate total if qty or rate changes
        if (field === 'qty' || field === 'rate') {
            const qty = parseFloat(item.qty) || 0;
            const rate = parseFloat(item.rate) || 0;
            item.total = qty * rate;
        }

        setReviewData(newReviewData);
    };

    const handleFinalSubmit = async () => {
        const finalPriorityId = selectedPriority?.value ?? currentPO.priorityId;

        // Ensure we have a date and tillDate
        const today = new Date().toISOString().split('T')[0];
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const fallbackTillDate = nextWeek.toISOString().split('T')[0];

        const payload = {
            projectId: Number(currentPO.projectId),
            reqId: Number(requestId),
            date: currentPO.date || today,
            tillDate: currentPO.tillDate || fallbackTillDate,
            particular: currentPO.particular,
            notification: "on",
            priorityId: (finalPriorityId !== null && finalPriorityId !== undefined) ? Number(finalPriorityId) : null,
            vendors: reviewData.map(group => ({
                vendorId: Number(group.vendorId),
                reqVendorId: Number(group.reqVendorId),
                items: group.items.map(item => ({
                    reqItemId: Number(item.reqItemId),
                    productId: Number(item.productId),
                    description: item.productDescription || "",
                    size: item.size || "",
                    qty: Number(item.qty),
                    rate: Number(item.rate),
                    unitId: Number(item.unitId)
                }))
            }))
        };


        if (!payload.projectId) {
            console.error("Missing Project ID in payload!", currentPO);
            Swal.fire('Error', 'Project ID is missing. Cannot proceed.', 'error');
            return;
        }

        try {
            setLoadingText('Creating Purchase Orders...');
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.post(CREATE_FINAL_PO_API, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const poIdList = response.data.poId || [];

                // --- WhatsApp Notification Logic ---
                try {
                    const poList = Array.isArray(poIdList) ? poIdList : [];
                    console.log("Processing POs for WhatsApp:", poList);

                    if (poList.length > 0) {
                        // Batch state changes to prevent background jump
                        setWhatsappLoading(true);
                        setWhatsappProgress({ current: 0, total: poList.length, vendorName: '' });
                        setShowReviewModal(false);
                        setLoading(false);

                        for (let i = 0; i < poList.length; i++) {
                            const poItem = poList[i];
                            const poId = poItem.poId;

                            // Robust vendor matching
                            const vendorMatch = reviewData.find(v =>
                                Number(v.vendorId) === Number(poItem.vendorId || poItem.VendorId)
                            ) || reviewData[i] || { vendorName: 'Vendor', whatsappNumber: '' };

                            setWhatsappProgress({
                                current: i + 1,
                                total: poList.length,
                                vendorName: vendorMatch.vendorName
                            });

                            if (poId) {
                                const whatsappPayload = {
                                    message: `Hello ${vendorMatch.vendorName},\n\nPlease find the attached Purchase Order #${poItem.poNumber || poId}.\n\nRegards,\nTeam.`,
                                    file: `${BASE_API}/uploads/po/PO-${poId}.pdf`
                                };

                                // Use SEND_WHATSAPP_PDF_API as requested by user
                                const targetApi = SEND_WHATSAPP_PDF_API(poId);
                                console.log(`POSTING to WhatsApp PDF API: ${targetApi}`, whatsappPayload);

                                try {
                                    const waRes = await axios.post(targetApi, whatsappPayload, {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                    console.log(`WhatsApp PDF API Success for ${vendorMatch.vendorName}:`, waRes.data);
                                } catch (waErr) {
                                    console.error(`WhatsApp PDF API Error for ${vendorMatch.vendorName}:`, waErr.response?.data || waErr.message);
                                }
                            }
                        }
                        setWhatsappLoading(false);
                    } else {
                        setShowReviewModal(false);
                        setLoading(false);
                    }
                } catch (waGlobalErr) {
                    console.error("WhatsApp Automation Global Error:", waGlobalErr);
                    setWhatsappLoading(false);
                    setShowReviewModal(false);
                    setLoading(false);
                }

                Swal.fire({
                    title: 'Success!',
                    text: 'Purchase Order created successfully and PDF sent to vendors!',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                }).then(() => {
                    navigate('/compare-po');
                });
            } else {
                Swal.fire('Error', response.data.message || 'Failed to create PO.', 'error');
            }
        } catch (error) {
            console.error('Create PO Error:', error);
            Swal.fire(
                'Error',
                error.response?.data?.error || error.response?.data?.message || 'An error occurred while submitting quotations.',
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    // Only show full-page initial loader if data isn't ready
    if (loading && !currentPO) {
        return <Loader fullPage text={loadingText} />;
    }

    if (!currentPO) {
        return (
            <div className="dashboard">
                <div className="p-8 text-center">
                    <MdInfo size={40} color="#9ca3af" />
                    <h3>Request not found</h3>
                    <button onClick={() => navigate('/compare-po')} className="back-btn-simple">Back to List</button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard compare-po-page">
            <div className="page-header">
                <button onClick={() => navigate('/compare-po')} className="back-btn">
                    <MdArrowBack />
                </button>
            </div>

            <div className="vendor-grid-view">
                <h2 className="section-title">Compare PO Details</h2>
                <div className="detail-view-header-detail-compare-po">
                    <div className="header-info-center">
                        <h3 className="section-title po-title" style={{ fontFamily: 'monospace' }}>#{currentPO.requestNumber}</h3>
                        <p className="po-subtitle">Project: {currentPO.project}</p>
                    </div>
                    <div style={{ width: '200px' }} className="header-spacer"></div>
                </div>

                <div className="design-table-wrapper comparison-view">
                    <table className="design-table">
                        <thead>
                            <tr>
                                <th>Item Name</th>
                                <th style={{ textAlign: 'center' }}>Unit</th>
                                <th style={{ textAlign: 'center' }}>Qty</th>
                                <th style={{ textAlign: 'center' }}>Size</th>
                                {vendors.map(vendor => (
                                    <th key={vendor.id} className="vendor-header-cell" style={{ textAlign: 'center' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                            <span className="vendor-sub">{vendor.name}</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 'normal' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={items.every(item => selectedItems[item.id]?.includes(vendor.id))}
                                                    onChange={() => handleVendorSelectAll(vendor.id)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <span>Select All</span>
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => {
                                const lowest = getLowestRate(item.id);
                                return (
                                    <tr key={item.id}>
                                        <td style={{ fontWeight: 600 }}>{item.name}</td>
                                        <td style={{ textAlign: 'center' }}>{item.unit}</td>
                                        <td style={{ textAlign: 'center' }}>{item.qty}</td>
                                        <td style={{ textAlign: 'center', color: '#64748b' }}>{item.size}</td>
                                        {vendors.map(vendor => {
                                            const rate = quotes[item.id] ? quotes[item.id][vendor.id] : 0;
                                            const isLowest = rate > 0 && rate === lowest;
                                            const isSelected = selectedItems[item.id]?.includes(vendor.id);

                                            return (
                                                <td key={vendor.id} className={`price-cell ${isLowest ? 'lowest-price' : ''}`} style={{ textAlign: 'center' }}>
                                                    {rate > 0 ? (
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!!isSelected}
                                                                onChange={() => handleItemSelect(item.id, vendor.id)}
                                                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                            />
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                <span>₹{rate.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                                                {!isLowest && rate > 0 && lowest > 0 && (
                                                                    <span style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px', fontWeight: '500' }}>
                                                                        (+₹{(rate - lowest).toLocaleString('en-US', { minimumFractionDigits: 2 })})
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#9ca3af' }}>-</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="compare-footer">
                    <div className="legend-section">
                        <div className="legend-item">
                            <MdCheckCircle color="#22c55e" size={16} />
                            <span>Green prices indicate lowest bid per line item</span>
                        </div>
                    </div>

                    <div className="selection-section" style={{ justifyContent: 'flex-end' }}>
                        <button
                            className="approve-btn-large"
                            disabled={!hasItemSelected}
                            onClick={handleReviewClick}
                        >
                            {hasItemSelected ? `Approve Order` : "Select Minimum 1 Item to Approve"} <MdArrowForward />
                        </button>
                    </div>
                </div>
            </div>

            {/* Review & Edit Modal */}
            {showReviewModal && (
                <div className="modal-overlay">
                    <div className="modal-content large-modal">
                        <div className="modal-header">
                            <h3>Review & Edit Purchase Order</h3>
                            <button className="close-btn" onClick={() => setShowReviewModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p className="modal-description">Please review the details below. You can edit Quantity, Rate, Unit, Size and Priority for notification before creating the PO.</p>

                            <div className="priority-selection-section" style={{ marginBottom: '20px', backgroundColor: '#f9fafb', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <label style={{ fontWeight: 600, minWidth: '100px' }}>Priority for notification :</label>
                                    <div style={{ flex: 1, maxWidth: '300px' }}>
                                        <Select
                                            options={priorities}
                                            value={selectedPriority}
                                            onChange={(selected) => setSelectedPriority(selected)}
                                            placeholder="Select Priority"
                                            classNamePrefix="react-select"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {reviewData.map((vendor, vIndex) => (
                                <div key={vendor.vendorId} className="vendor-review-section">
                                    <h4 className="vendor-review-title">{vendor.vendorName}</h4>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table className="design-table inner-table" style={{ width: '100%', minWidth: '600px' }}>
                                            <thead>
                                                <tr>
                                                    <th>Item</th>
                                                    <th style={{ width: '80px' }}>Size</th>
                                                    <th style={{ width: '80px' }}>Unit</th>
                                                    <th style={{ width: '80px' }}>Qty</th>
                                                    <th style={{ width: '100px' }}>Rate</th>
                                                    <th style={{ width: '100px' }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {vendor.items.map((item, iIndex) => (
                                                    <tr key={iIndex}>
                                                        <td>
                                                            <div className="item-name-text">{item.productName}</div>
                                                            {/* <div className="item-desc-text">{item.productDescription}</div> */}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className="table-input"
                                                                value={item.size}
                                                                onChange={(e) => handleReviewChange(vIndex, iIndex, 'size', e.target.value)}
                                                            />
                                                        </td>
                                                        <td style={{ width: '150px' }}>
                                                            <Select
                                                                options={units.map(u => ({ value: u.id, label: u.unitName }))}
                                                                value={item.unitId ? { value: item.unitId, label: item.unit } : null}
                                                                onChange={(selectedOption) => {
                                                                    handleReviewChange(vIndex, iIndex, 'unitId', selectedOption.value);
                                                                    handleReviewChange(vIndex, iIndex, 'unit', selectedOption.label);
                                                                }}
                                                                placeholder="Select Unit"
                                                                classNamePrefix="react-select"
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                styles={{
                                                                    control: (base) => ({
                                                                        ...base,
                                                                        minHeight: '35px',
                                                                        fontSize: '12px',
                                                                        borderColor: '#d1d5db',
                                                                        '&:hover': { borderColor: '#6366f1' }
                                                                    }),
                                                                    menu: (base) => ({
                                                                        ...base,
                                                                        fontSize: '12px',
                                                                        zIndex: 9999
                                                                    }),
                                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                                }}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="table-input"
                                                                value={item.qty}
                                                                onChange={(e) => handleReviewChange(vIndex, iIndex, 'qty', e.target.value)}
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="table-input"
                                                                value={item.rate}
                                                                onChange={(e) => handleReviewChange(vIndex, iIndex, 'rate', e.target.value)}
                                                                min="0"
                                                            />
                                                        </td>
                                                        <td className="font-bold">
                                                            ₹{parseFloat(item.total || 0).toLocaleString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* <div className="vendor-subtotal">
                                        Subtotal: ₹{vendor.items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toLocaleString()}
                                    </div> */}
                                </div>
                            ))}

                            <div className="modal-actions">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setShowReviewModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-confirm"
                                    onClick={handleFinalSubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Confirm & Create PO'} <MdCheckCircle />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Overlaid Loader (for PO Creation) */}
            {loading && (
                <Loader fullPage text={loadingText} />
            )}

            {/* WhatsApp Notification Loader Modal */}
            {whatsappLoading && (
                <div className="whatsapp-loader-overlay">
                    <div className="whatsapp-loader-card">
                        <div className="whatsapp-icon-wrapper">
                            <FaWhatsapp className="whatsapp-anim-icon" size={50} />
                            <div className="pulse-ring"></div>
                        </div>
                        <h3>Sending Status</h3>
                        <p>Notifying vendors via WhatsApp...</p>
                        <div className="wa-progress-container">
                            <div className="wa-progress-bar">
                                <div
                                    className="wa-progress-fill"
                                    style={{ width: `${(whatsappProgress.current / whatsappProgress.total) * 100}%` }}
                                ></div>
                            </div>
                            <div className="wa-progress-stats">
                                <span>{whatsappProgress.vendorName || 'Initializing...'}</span>
                                <span>{whatsappProgress.current} / {whatsappProgress.total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComparePODetail;
